import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, PencilLine, Plus, RefreshCw, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { useConfirmDialog } from '@/components/common/confirm-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { getErrorMessage } from '@/lib/api'
import {
  addCustomTemplateToState,
  assertUniqueNewTemplatePurposeKey,
  collectDistinctTemplateKeys,
  DEFAULT_TEMPLATE_PURPOSE,
  type ServiceInstanceSignupTemplatesState,
  type TemplateChannel,
  type TemplateEditorContext,
  type TemplateScope,
  isCustomTemplateStorageId,
  isSignupTemplateConfigured,
  isSignupTemplateVisibleInContext,
  isValidCustomTemplateSlug,
  isValidTemplatePurpose,
  mergeServiceInstanceSignupTemplates,
  normalizeCustomTemplateSlug,
  parseServiceInstanceSignupTemplates,
  parseTemplateStorageId,
  removeTemplateStorageIdFromState,
  signupTemplateDefinitions,
  signupTemplatePlaceholderTokens,
  templateChannelDefinitions,
} from '@/lib/service-instance-templates'
import { type OrgTreeSignupTemplatePreview, orgTreeApi } from '@/services/org-tree'

type TemplateEditorState = {
  channel: TemplateChannel
  templateKey: string
  draft: ServiceInstanceSignupTemplatesState['emails'][string]
    | ServiceInstanceSignupTemplatesState['sms'][string]
    | ServiceInstanceSignupTemplatesState['push'][string]
}

const templateFieldsByChannel: Record<TemplateChannel, Array<{ key: string; label: string; multiline?: boolean; rows?: number }>> = {
  emails: [
    { key: 'subject', label: 'Subject' },
    { key: 'heading', label: 'Heading' },
    { key: 'intro', label: 'Intro', multiline: true, rows: 6 },
  ],
  sms: [
    { key: 'message', label: 'Message', multiline: true, rows: 5 },
  ],
  push: [
    { key: 'title', label: 'Title' },
    { key: 'message', label: 'Message', multiline: true, rows: 5 },
  ],
}

function buildTemplatePreview(
  template: ServiceInstanceSignupTemplatesState[TemplateChannel][string],
) {
  const previewLines = Object.entries(template)
    .filter(([fieldKey]) => !['template_key', 'template_label', 'template_scope', 'template_purpose'].includes(fieldKey))
    .map(([, value]) => String(value ?? '').trim())
    .filter(Boolean)

  if (previewLines.length === 0) {
    return 'No custom content configured.'
  }

  return previewLines.join(' • ')
}

const defaultNewTemplateChannelSelection = (): Record<TemplateChannel, boolean> => ({
  emails: true,
  sms: true,
  push: true,
})

const previewChannelMap: Record<TemplateChannel, 'email' | 'sms' | 'push'> = {
  emails: 'email',
  sms: 'sms',
  push: 'push',
}

export default function ServiceInstanceTemplateManager({
  serviceInstanceId,
  currentConfig,
  entityLabelLower,
  templateEditorContext = 'organization',
}: {
  serviceInstanceId: string | number | null | undefined
  currentConfig: Record<string, unknown> | null | undefined
  entityLabelLower: string
  templateEditorContext?: TemplateEditorContext
}) {
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const [templates, setTemplates] = useState<ServiceInstanceSignupTemplatesState>(() => parseServiceInstanceSignupTemplates(currentConfig))
  const [editorState, setEditorState] = useState<TemplateEditorState | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [newTemplateKey, setNewTemplateKey] = useState('')
  const [newTemplateLabel, setNewTemplateLabel] = useState('')
  const [newTemplatePurpose, setNewTemplatePurpose] = useState<string>(DEFAULT_TEMPLATE_PURPOSE)
  const [newTemplateChannels, setNewTemplateChannels] = useState<Record<TemplateChannel, boolean>>(defaultNewTemplateChannelSelection)
  const [newCoverageScope, setNewCoverageScope] = useState<TemplateScope>('organization')

  const previewTemplateMutation = useMutation({
    mutationFn: async (previewState: TemplateEditorState) => {
      if (!serviceInstanceId) {
        throw new Error('Missing service ID')
      }

      return orgTreeApi.previewServiceInstanceTemplate(serviceInstanceId, {
        channel: previewChannelMap[previewState.channel],
        template_key: previewState.templateKey,
        signup_label: previewState.templateKey,
        draft: Object.fromEntries(
          Object.entries(previewState.draft as Record<string, unknown>).filter(([key]) =>
            !['template_key', 'template_label', 'template_scope', 'template_purpose'].includes(key),
          ).map(([key, value]) => [key, String(value ?? '')]),
        ),
      })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  useEffect(() => {
    setTemplates(parseServiceInstanceSignupTemplates(currentConfig))
  }, [currentConfig])

  useEffect(() => {
    if (addOpen && templateEditorContext === 'branch') {
      setNewCoverageScope('branch')
    }
  }, [addOpen, templateEditorContext])

  useEffect(() => {
    if (!editorState) {
      previewTemplateMutation.reset()
      return
    }

    previewTemplateMutation.mutate(editorState)
  }, [editorState?.channel, editorState?.templateKey])

  const editorDefinition = useMemo(
    () => templateChannelDefinitions.find((definition) => definition.key === editorState?.channel) ?? null,
    [editorState?.channel],
  )

  const activeTemplateTitle = useMemo(() => {
    if (!editorState) {
      return null
    }
    const parsed = parseTemplateStorageId(editorState.templateKey)
    const builtin = parsed.builtin
      ? signupTemplateDefinitions.find((definition) => definition.key === parsed.templateKey)
      : undefined
    if (builtin) {
      return builtin.label
    }
    const row =
      templates.emails[editorState.templateKey]
      ?? templates.sms[editorState.templateKey]
      ?? templates.push[editorState.templateKey]
    const label = row?.template_label
    return label || row?.template_key || editorState.templateKey
  }, [editorState, templates])

  const visibleTemplateKeys = useMemo(() => {
    return collectDistinctTemplateKeys(templates).filter((storageId) => {
      const rowMeta = templates.emails[storageId] ?? templates.sms[storageId] ?? templates.push[storageId]
      return isSignupTemplateVisibleInContext(storageId, rowMeta, templateEditorContext)
    })
  }, [templates, templateEditorContext])

  const visibleKeysByChannel = useMemo(() => {
    const pick = (channel: TemplateChannel) =>
      visibleTemplateKeys.filter((storageId) => Boolean(templates[channel][storageId]))
    return {
      emails: pick('emails'),
      sms: pick('sms'),
      push: pick('push'),
    } satisfies Record<TemplateChannel, string[]>
  }, [templates, visibleTemplateKeys])

  const saveTemplateMutation = useMutation({
    mutationFn: async (nextTemplates: ServiceInstanceSignupTemplatesState) => {
      if (!serviceInstanceId) {
        throw new Error('Missing service ID')
      }

      const nextConfig = mergeServiceInstanceSignupTemplates(currentConfig, nextTemplates)
      return orgTreeApi.updateServiceInstance(serviceInstanceId, { public_config: nextConfig })
    },
    onSuccess: async (_data, nextTemplates) => {
      setTemplates(nextTemplates)
      setEditorState(null)
      setAddOpen(false)
      setNewTemplateKey('')
      setNewTemplateLabel('')
      setNewTemplatePurpose(DEFAULT_TEMPLATE_PURPOSE)
      setNewTemplateChannels(defaultNewTemplateChannelSelection())
      setNewCoverageScope(templateEditorContext === 'branch' ? 'branch' : 'organization')
      toast.success('Templates updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['org-tree-service-instance'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openEditor = (channel: TemplateChannel, templateKey: string) => {
    const currentTemplate = templates[channel][templateKey]
    if (!currentTemplate) {
      return
    }

    setEditorState({
      channel,
      templateKey,
      draft: { ...currentTemplate },
    })
  }

  const handleEditorFieldChange = (fieldKey: string, value: string) => {
    if (!editorState) {
      return
    }

    setEditorState({
      ...editorState,
      draft: {
        ...editorState.draft,
        [fieldKey]: value,
      },
    })
  }

  const handleSaveTemplate = () => {
    if (!editorState) {
      return
    }

    const nextTemplates: ServiceInstanceSignupTemplatesState = {
      ...templates,
      [editorState.channel]: {
        ...templates[editorState.channel],
        [editorState.templateKey]: editorState.draft as ServiceInstanceSignupTemplatesState[typeof editorState.channel][string],
      },
    }

    saveTemplateMutation.mutate(nextTemplates)
  }

  const handleConfirmAddTemplate = () => {
    const slug = normalizeCustomTemplateSlug(newTemplateKey)
    if (!isValidCustomTemplateSlug(slug)) {
      toast.error('Use a lowercase template key: start with a letter, then letters, digits, underscores or hyphens (max 64).')
      return
    }
    const purpose = newTemplatePurpose.trim().toLowerCase()
    if (!isValidTemplatePurpose(purpose)) {
      toast.error('Use a lowercase purpose id: letters, digits, underscores only (e.g. signup).')
      return
    }
    if (!assertUniqueNewTemplatePurposeKey(purpose, slug, templates)) {
      toast.error('This template key is already used for this purpose on this service instance.')
      return
    }
    const scope: TemplateScope = newCoverageScope === 'branch' ? 'branch' : 'organization'
    const selectedChannels = templateChannelDefinitions
      .map((definition) => definition.key)
      .filter((channel) => newTemplateChannels[channel])
    if (selectedChannels.length === 0) {
      toast.error('Select at least one channel (email, SMS, or push).')
      return
    }
    const next = addCustomTemplateToState(
      templates,
      purpose,
      slug,
      newTemplateLabel.trim() || slug,
      scope,
      selectedChannels,
    )
    saveTemplateMutation.mutate(next)
  }

  const handleDeleteCustomTemplate = (storageId: string) => {
    if (!isCustomTemplateStorageId(storageId)) {
      return
    }
    confirm({
      title: 'Remove custom template',
      description: 'Remove this custom template from every channel where it exists for this service instance?',
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: () => {
        const next = removeTemplateStorageIdFromState(templates, storageId)
        saveTemplateMutation.mutate(next)
      },
    })
  }

  const handleRefreshPreview = () => {
    if (!editorState) {
      return
    }

    previewTemplateMutation.mutate(editorState)
  }

  const renderPreviewContent = (preview: OrgTreeSignupTemplatePreview | null | undefined) => {
    if (!preview) {
      return <p className="text-sm text-muted-foreground">Preview will appear here after loading sample content.</p>
    }

    if (preview.channel === 'email') {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Subject</p>
            <p className="text-sm font-medium text-foreground">{preview.preview.subject || 'No subject'}</p>
          </div>
          <div className="rounded-[20px] border border-border/70 bg-background p-3">
            <div className="max-h-[360px] overflow-auto" dangerouslySetInnerHTML={{ __html: preview.preview.html_content || '' }} />
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {preview.preview.title ? (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Title</p>
            <p className="text-sm font-medium text-foreground">{preview.preview.title}</p>
          </div>
        ) : null}
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Message</p>
          <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{preview.preview.message || 'No message'}</p>
        </div>
      </div>
    )
  }

  if (!serviceInstanceId) {
    return (
      <Card className="rounded-[24px] border-border/80">
        <CardHeader>
          <CardTitle>Templates</CardTitle>
          <CardDescription>Select a {entityLabelLower} before editing templates.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <>
      <ConfirmDialog />
      <div className="space-y-6">
        <Card className="rounded-[28px] border-border/80">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Signup Templates</CardTitle>
              <CardDescription>
                Manage structured multilingual content for signup emails, SMS, and push notifications. Use placeholders like {signupTemplatePlaceholderTokens.slice(0, 4).join(', ')}.
              </CardDescription>
            </div>
            <Button
              type="button"
              className="shrink-0 rounded-full"
              onClick={() => {
                setNewTemplatePurpose(DEFAULT_TEMPLATE_PURPOSE)
                setNewTemplateChannels(defaultNewTemplateChannelSelection())
                setNewCoverageScope(templateEditorContext === 'branch' ? 'branch' : 'organization')
                setAddOpen(true)
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add template
            </Button>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {signupTemplatePlaceholderTokens.map((token) => (
              <Badge key={token} variant="outline" className="rounded-full px-3 py-1 font-mono text-[11px]">
                {token}
              </Badge>
            ))}
          </CardContent>
        </Card>

        {templateChannelDefinitions.map((channelDefinition) => (
          <section key={channelDefinition.key} className="space-y-4">
            <div className="space-y-1 px-1">
              <h2 className="text-xl font-semibold text-foreground">{channelDefinition.label}</h2>
              <p className="text-sm text-muted-foreground">{channelDefinition.description}</p>
            </div>

            <div className="grid gap-4 xl:grid-cols-3 md:grid-cols-2">
              {visibleKeysByChannel[channelDefinition.key].map((storageId) => {
                const template = templates[channelDefinition.key][storageId]
                const metaRow =
                  templates.emails[storageId] ?? templates.sms[storageId] ?? templates.push[storageId]
                const parsed = parseTemplateStorageId(storageId)
                const builtin = parsed.builtin
                  ? signupTemplateDefinitions.find((definition) => definition.key === parsed.templateKey)
                  : undefined
                const cardTitle = builtin?.label ?? metaRow?.template_label ?? metaRow?.template_key ?? storageId
                const cardDescription =
                  builtin?.description
                  ?? (metaRow?.template_scope
                    ? `Custom · scope: ${metaRow.template_scope}`
                    : 'Custom template')
                const configured = isSignupTemplateConfigured(template)
                const showDelete = isCustomTemplateStorageId(storageId)

                return (
                  <Card key={`${channelDefinition.key}-${storageId}`} className="rounded-[24px] border-border/80">
                    <CardHeader className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <CardTitle className="text-base">{cardTitle}</CardTitle>
                          <CardDescription>{cardDescription}</CardDescription>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline" className="w-fit rounded-md px-2 py-0.5 font-mono text-[11px] font-normal tracking-tight">
                              {storageId}
                            </Badge>
                            {!parsed.builtin ? (
                              <Badge variant="secondary" className="text-[11px] font-normal">
                                {metaRow?.template_purpose ?? 'signup'}
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                        <Badge variant={configured ? 'secondary' : 'outline'}>
                          {configured ? 'Configured' : 'Default'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="line-clamp-4 min-h-[4.5rem] text-sm leading-6 text-muted-foreground">
                        {buildTemplatePreview(template)}
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button type="button" variant="outline" className="w-full sm:flex-1" onClick={() => openEditor(channelDefinition.key, storageId)}>
                          <PencilLine className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        {showDelete ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full border-destructive/40 text-destructive hover:bg-destructive/10 sm:w-auto"
                            onClick={() => handleDeleteCustomTemplate(storageId)}
                            disabled={saveTemplateMutation.isPending}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      <Dialog open={Boolean(editorState)} onOpenChange={(open) => {
        if (!open && !saveTemplateMutation.isPending) {
          setEditorState(null)
        }
      }}>
        <DialogContent
          showCloseButton
          className="flex max-h-[min(90dvh,880px)] w-[calc(100%-2rem)] max-w-4xl flex-col gap-0 overflow-hidden rounded-[28px] border-border/80 p-0 sm:max-w-4xl"
        >
          <div className="shrink-0 border-b border-border/70 px-6 py-5 pr-14">
            <DialogHeader>
              <DialogTitle>
                Edit {activeTemplateTitle ?? 'template'}{editorDefinition ? ` · ${editorDefinition.label}` : ''}
              </DialogTitle>
              <DialogDescription>
                Content is saved for this channel only. Leave fields empty to fall back to default template behavior.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="grid gap-6 px-6 py-5 lg:grid-cols-[minmax(0,1.1fr),minmax(0,0.9fr)]">
              <div className="space-y-4">
                {editorState ? templateFieldsByChannel[editorState.channel].map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={`template-${editorState.channel}-${editorState.templateKey}-${field.key}`}>{field.label}</Label>
                    {field.multiline ? (
                      <Textarea
                        id={`template-${editorState.channel}-${editorState.templateKey}-${field.key}`}
                        rows={field.rows ?? 4}
                        className="min-h-[4.5rem] max-h-48 resize-y"
                        value={String(editorState.draft[field.key as keyof typeof editorState.draft] ?? '')}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => handleEditorFieldChange(field.key, event.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    ) : (
                      <Input
                        id={`template-${editorState.channel}-${editorState.templateKey}-${field.key}`}
                        value={String(editorState.draft[field.key as keyof typeof editorState.draft] ?? '')}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => handleEditorFieldChange(field.key, event.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    )}
                  </div>
                )) : null}

                <details className="rounded-[16px] border border-border/70 bg-muted/15 px-4 py-3">
                  <summary className="cursor-pointer text-sm font-medium text-foreground">Supported placeholders</summary>
                  <p className="mt-2 text-xs leading-6 text-muted-foreground">
                    {signupTemplatePlaceholderTokens.join(', ')}
                  </p>
                </details>
              </div>

              <div className="flex flex-col gap-4 rounded-[20px] border border-border/70 bg-muted/10 p-4 lg:max-h-[min(58vh,520px)] lg:overflow-hidden">
                <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">Sample preview</p>
                    <p className="text-xs text-muted-foreground">Rendered with sample signup data.</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={handleRefreshPreview} disabled={previewTemplateMutation.isPending || !editorState}>
                    {previewTemplateMutation.isPending ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                    Refresh
                  </Button>
                </div>

                <div className="min-h-[200px] flex-1 overflow-y-auto rounded-[14px] border border-border/70 bg-background p-3 lg:min-h-0">
                  {previewTemplateMutation.isPending && !previewTemplateMutation.data ? (
                    <p className="text-sm text-muted-foreground">Loading preview...</p>
                  ) : renderPreviewContent(previewTemplateMutation.data)}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-3 border-t border-border/70 px-6 py-4 sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setEditorState(null)} disabled={saveTemplateMutation.isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveTemplate} disabled={saveTemplateMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {saveTemplateMutation.isPending ? 'Saving...' : 'Save template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open)
          if (!open) {
            setNewTemplateKey('')
            setNewTemplateLabel('')
            setNewTemplatePurpose(DEFAULT_TEMPLATE_PURPOSE)
            setNewTemplateChannels(defaultNewTemplateChannelSelection())
            setNewCoverageScope(templateEditorContext === 'branch' ? 'branch' : 'organization')
          }
        }}
      >
        <DialogContent className="max-w-lg rounded-[28px] border-border/80">
          <DialogHeader>
            <DialogTitle>Add template</DialogTitle>
            <DialogDescription>
              New templates are saved on this service instance. Choose purpose, channels, and visibility (organisation vs this branch).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[min(70dvh,calc(100vh-8rem))] space-y-4 overflow-y-auto py-2 pr-1">
            <div className="space-y-2">
              <Label htmlFor="new-template-purpose">Purpose</Label>
              <Input
                id="new-template-purpose"
                className="font-mono text-sm"
                placeholder="Any id, e.g. signup, order_confirmation, welcome_series"
                value={newTemplatePurpose}
                onChange={(e) => setNewTemplatePurpose(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, digits, underscores (starts with a letter). Stored with your template key as{' '}
                <span className="font-mono">purpose:template_key</span>.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-template-key">Template key</Label>
              <Input
                id="new-template-key"
                className="font-mono text-sm"
                placeholder="e.g. retail_signup_v2"
                value={newTemplateKey}
                onChange={(e) => setNewTemplateKey(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Unique per purpose on this instance. Example id:{' '}
                <span className="font-mono">
                  {(newTemplatePurpose.trim().toLowerCase() && isValidTemplatePurpose(newTemplatePurpose.trim().toLowerCase())
                    ? newTemplatePurpose.trim().toLowerCase()
                    : 'purpose')}:your_key
                </span>
                .
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-template-label">Display label</Label>
              <Input
                id="new-template-label"
                placeholder="Shown in the admin UI"
                value={newTemplateLabel}
                onChange={(e) => setNewTemplateLabel(e.target.value)}
              />
            </div>
            <div className="space-y-3">
              <div>
                <Label id="new-template-channels-label">Channels</Label>
                <p id="new-template-channels-hint" className="mt-1 text-xs text-muted-foreground">
                  Select which delivery types get an entry for this template. At least one is required.
                </p>
              </div>
              <div className="space-y-3 rounded-[18px] border border-border/70 p-4" role="group" aria-labelledby="new-template-channels-label" aria-describedby="new-template-channels-hint">
                {templateChannelDefinitions.map((definition) => (
                  <div key={definition.key} className="flex items-start gap-3">
                    <Checkbox
                      id={`new-template-channel-${definition.key}`}
                      checked={newTemplateChannels[definition.key]}
                      onCheckedChange={(checked) =>
                        setNewTemplateChannels((prev) => ({
                          ...prev,
                          [definition.key]: checked === true,
                        }))}
                    />
                    <div className="space-y-0.5 leading-none">
                      <Label htmlFor={`new-template-channel-${definition.key}`} className="cursor-pointer font-medium">
                        {definition.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{definition.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <Label>Visibility</Label>
              <RadioGroup
                value={newCoverageScope}
                onValueChange={(v) => setNewCoverageScope(v as TemplateScope)}
                className="gap-3"
              >
                <div className="flex items-start gap-3 rounded-[18px] border border-border/70 p-3">
                  <RadioGroupItem value="organization" id="coverage-org" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="coverage-org" className="cursor-pointer font-medium leading-none">Whole organisation</Label>
                    <p className="text-xs text-muted-foreground">Organisation-scoped visibility across branches.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[18px] border border-border/70 p-3">
                  <RadioGroupItem value="branch" id="coverage-branch" className="mt-1" />
                  <div className="space-y-1">
                    <Label htmlFor="coverage-branch" className="cursor-pointer font-medium leading-none">This branch only</Label>
                    <p className="text-xs text-muted-foreground">Only in branch workspace contexts.</p>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={saveTemplateMutation.isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirmAddTemplate} disabled={saveTemplateMutation.isPending}>
              {saveTemplateMutation.isPending ? 'Saving…' : 'Create template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
